import React from 'react';
import { Box, Modal, BoxProps, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

export interface BlankModalProps {
  isSmallDevice?: boolean;
  open: boolean;
  handleClose: () => void;
  sx?: BoxProps['sx'];
  showCloseButton?: boolean;
  fullScreen?: boolean;
  children?: JSX.Element | JSX.Element[];
  ariaLabelledBy?: string;
}

/**
 * Renders an Blank Modal component styled with some sensible defaults
 * @param isSmallDevice - indicates whether on a small-width device, if true, will take up the full screen
 * @param open - if true, the component is shown
 * @param handleClose - handler for when the modal is closed (esc pressed or click on greyed out area)
 * @param children - the content for the modal
 * @param sx - optional, styles to apply to a MUI Box that wraps the html form component
 * @param showCloseButton - optional defaults to true, if false the Close IconButton will not be shown
 * @param fullScreen - if true, will take up the full screen
 * @param ariaLabelledBy - the modal's aria-labelledby property
 */
const BlankModal = ({
  isSmallDevice,
  open,
  handleClose,
  children,
  sx,
  showCloseButton = true,
  ariaLabelledBy = '',
}: BlankModalProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={handleClose} aria-labelledby={ariaLabelledBy} data-testid="blank-modal">
      <Box
        sx={
          isSmallDevice
            ? {
                position: 'fixed',
                top: 0,
                bottom: 0,
                width: '100%',
                background: '#fff',
                zIndex: 999,
                overflowY: 'scroll',
                backgroundColor: 'common.white',
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }
            : {
                position: 'absolute',
                width: '90%',
                maxWidth: (theme) => `${theme.breakpoints.values.sm}px`,
                height: 'auto',
                maxHeight: '90%',
                overflowY: 'scroll',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%)`,
                backgroundColor: 'common.white',
                borderRadius: '10px',
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                ...sx,
              }
        }
      >
        <>
          {showCloseButton && (
            <Box position="absolute" top="16px" right="16px">
              <IconButton onClick={handleClose} aria-label={t('ns.common:modal:close_button:aria_label')}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {children}
        </>
      </Box>
    </Modal>
  );
};

export default BlankModal;
